<template>
  <div class="overlay-frame" :style="{ zIndex }" role="presentation">
    <div class="overlay-frame__scrim" @click="handleScrimClick" />
    <div class="overlay-frame__wrap" :class="`overlay-frame__wrap--${placement}`" role="presentation">
      <section
        ref="panelRef"
        class="overlay-frame__panel"
        :class="{ 'overlay-frame__panel--wide': wide }"
        role="dialog"
        :aria-modal="ariaModal"
        @keydown.capture="handleKeydown"
        @click.stop
      >
        <header v-if="title || $slots.header" class="overlay-frame__header">
          <slot name="header">
            <div class="overlay-frame__title">
              <span v-if="icon" class="overlay-frame__icon" aria-hidden="true">{{ icon }}</span>
              <div style="min-width: 0;">
                <div class="overlay-frame__title-text">{{ title }}</div>
                <div v-if="subtitle" class="overlay-frame__subtitle text-muted">{{ subtitle }}</div>
              </div>
            </div>
            <UiIconButton variant="ghost" size="sm" @click="emit('close')" :aria-label="closeLabel">
              {{ ICONS.actions.close }}
            </UiIconButton>
          </slot>
        </header>

        <div class="overlay-frame__body">
          <slot />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { ICONS } from "../../lib/icons";
import UiIconButton from "../ui/UiIconButton.vue";

const props = withDefaults(
  defineProps<{
    zIndex?: number;
    title?: string | null;
    subtitle?: string | null;
    icon?: string | null;
    closeLabel?: string;
    closeOnScrim?: boolean;
    ariaModal?: boolean;
    placement?: "center" | "top";
    wide?: boolean;
  }>(),
  {
    zIndex: 4000,
    title: null,
    subtitle: null,
    icon: null,
    closeLabel: "Close",
    closeOnScrim: true,
    ariaModal: true,
    placement: "center",
    wide: false
  }
);

const emit = defineEmits<{ close: [] }>();

const zIndex = computed(() => props.zIndex);
const panelRef = ref<HTMLElement | null>(null);
let restoreFocusEl: HTMLElement | null = null;

function focusableElements(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(
    root.querySelectorAll<HTMLElement>(
      [
        "[autofocus]",
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        "[tabindex]:not([tabindex='-1'])"
      ].join(",")
    )
  );
  return nodes.filter((el) => {
    const style = window.getComputedStyle(el);
    return style.visibility !== "hidden" && style.display !== "none";
  });
}

function focusFirst() {
  const panel = panelRef.value;
  if (!panel) return;
  const focusables = focusableElements(panel);
  focusables[0]?.focus();
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== "Tab") return;
  const panel = panelRef.value;
  if (!panel) return;
  const focusables = focusableElements(panel);
  if (focusables.length === 0) {
    event.preventDefault();
    return;
  }
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (event.shiftKey) {
    if (!active || active === first || !panel.contains(active)) {
      event.preventDefault();
      last.focus();
    }
    return;
  }

  if (!active || active === last || !panel.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

function handleScrimClick() {
  if (!props.closeOnScrim) return;
  emit("close");
}

onMounted(() => {
  restoreFocusEl = (document.activeElement as HTMLElement | null) ?? null;
  // Allow child overlays (like command palette) to focus themselves first.
  window.setTimeout(() => {
    if (!panelRef.value) return;
    if (panelRef.value.contains(document.activeElement)) return;
    focusFirst();
  }, 0);
});

onBeforeUnmount(() => {
  const el = restoreFocusEl;
  restoreFocusEl = null;
  if (!el) return;
  window.setTimeout(() => el.focus?.(), 0);
});
</script>

<style scoped>
.overlay-frame {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
}

.overlay-frame__scrim {
  position: absolute;
  inset: 0;
  background: var(--overlay-scrim);
  backdrop-filter: blur(6px);
}

.overlay-frame__wrap {
  position: relative;
  width: 100%;
  display: grid;
  place-items: center;
  padding-top: 0;
}

.overlay-frame__wrap--top {
  place-items: start center;
  padding-top: 10vh;
}

.overlay-frame__panel {
  width: min(680px, 94vw);
  max-height: min(86vh, 720px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: color-mix(in srgb, var(--bg-2) 92%, transparent);
  border: 1px solid var(--border-1);
  border-radius: 24px;
  box-shadow: var(--shadow-2);
}

.overlay-frame__panel--wide {
  width: min(920px, 96vw);
}

.overlay-frame__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid color-mix(in srgb, var(--border-0) 85%, transparent);
  background:
    radial-gradient(circle at 20% 0%, color-mix(in srgb, var(--accent) 12%, transparent), transparent 52%),
    linear-gradient(180deg, var(--glass-1), transparent 65%);
}

.overlay-frame__title {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  min-width: 0;
}

.overlay-frame__icon {
  width: 44px;
  height: 44px;
  border-radius: var(--radius-2);
  display: grid;
  place-items: center;
  background: var(--surface-ghost-bg);
  border: 1px solid color-mix(in srgb, var(--accent) 18%, var(--border-0));
  box-shadow: var(--shadow-0);
  font-size: 1.25rem;
}

.overlay-frame__title-text {
  font-family: var(--font-display);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  line-height: 1.1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.overlay-frame__subtitle {
  margin-top: 0.2rem;
  font-size: 0.9rem;
}

.overlay-frame__body {
  padding: 1rem;
  overflow: auto;
}
</style>
